import { Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { __CERTIFICATION_API_URL__ } from 'shell/constants/environment';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import Form, { ReleaseModeForm } from 'front-office/components/release-mode/form';
import { HttpResponse } from 'shell/models/http-response';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { transformReleaseMode } from 'front-office/components/release-mode/utils/transform-release-mode';
import { ReleaseModeCrumbs } from 'front-office/constants/release-mode-crumbs';
import { ReleaseMode } from 'shell/models/release-mode';
import { useEventCallback } from '@mui/material';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';

const ReleaseModeEdit = (): React.ReactElement => {
  const params = useParams();

  const [releaseMode] = useHttpFetch<HttpResponse<ReleaseMode>, ReleaseModeForm>(
    `${__CERTIFICATION_API_URL__}/certificazione/modalita-rilascio/${params.id}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: transformReleaseMode,
    }
  );

  const updateReleaseMode = useHttpClient(
    `${__CERTIFICATION_API_URL__}/certificazione/modalita-rilascio/${params.id}`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
        data: {},
      },
    }
  );

  const navigate = useNavigate();

  const onOk = useEventCallback((form: ReleaseModeForm | undefined) => {
    (
      updateReleaseMode({
        data: {
          tipoRegistro: form?.register || undefined,
          descrizione: form?.descriptionRelease || undefined,
          descrizioneModalitaStampa: form?.descriptionPrint || undefined,
          codiceEsenzioneAnpr: form?.exemption || undefined,
          importoImpostaBollo: form?.typePayment?.stamp || undefined,
          importoDirittiSegreteria: form?.typePayment?.secretariat || undefined,
          importoDirittiStatoCivile: form?.typePayment?.maritalStatus || undefined,
          importoDirittiFissi: form?.typePayment?.fixed || undefined,
          importoSpese: form?.typePayment?.expenses || undefined,
          codiceImpDirittiStatoCivile: form?.typePayment?.maritalStatusCode || undefined,
          codiceImpDirittiFissi: form?.typePayment?.fixedCode || undefined,
          codiceImpImpostaBollo: form?.typePayment?.stampCode || undefined,
          codiceImpSpese: form?.typePayment?.expensesCode || undefined,
          codiceImpDirittiSegreteria: form?.typePayment?.secretariatCode || undefined,
          dataInizioValidita: form?.dateFrom ? form?.dateFrom?.toFormat('yyyy-MM-dd') : undefined,
          dataFineValidita: form?.dateTo ? form?.dateTo?.toFormat('yyyy-MM-dd') : undefined,
          codiceUsoModalita: form?.use || undefined,
          key: {
            codiceModalitaRilascio: form?.code || undefined,
          },
        },
      }) as ResponsePromise<void, void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      navigate('/sportello/modalita-rilascio');
    });
  });

  const loading = releaseMode.isLoading || releaseMode.pristine || releaseMode.hasErrors;

  return (
    <Fragment>
      <Breadcrumbs items={[...ReleaseModeCrumbs, { name: 'Modifica modalità rilascio' }]} />
      {loading ? null : (
        <Form
          title="Modifica modalità rilascio"
          onOk={onOk}
          isEdit
          initialValues={releaseMode.data as ReleaseModeForm}
          onCancel={() => {
            navigate('/sportello/modalita-rilascio');
          }}
        />
      )}
    </Fragment>
  );
};

ReleaseModeEdit.displayName = 'Release Mode edit page component';

export default ReleaseModeEdit;
